import React, {useCallback, useEffect, useState} from "react";
import "./Export.css";
import {Button, Checkbox, Flex, Modal, Radio, Tag, Typography, Steps} from "antd";
import {SyncOutlined, CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, InfoCircleOutlined, DownloadOutlined, EyeOutlined} from '@ant-design/icons';
import {getRoutes} from "../services/routeService";
import {onError} from "../libs/errorLib";
import fileDownload from 'js-file-download';
import csvStringify from 'csv-stringify';
import dayjs from "../dayjs";

import {buildGtfsZip, buildHRMtoZip, getRoutesAsFlatArray, publishTfnsw, buildTodisZip} from "../libs/exportLib";
import {useParams} from "react-router-dom";
import Nav from "react-bootstrap/Nav";
import {
    deadrunModelData,
    driverShiftModelData,
    employeeModelData,
    publishTfNSWModelData,
    routeModelData,
    scheduleModelData,
    shiftBatModelData,
    stopModelData,
    vehicleShiftModelData
} from "../services/ModelService";
import {Collapse} from "antd/lib";
import {last} from "lodash/array";
import {values} from "lodash/object"
import {Download} from "react-bootstrap-icons";
import config from "../config";
import {FEATURE} from "../model/features";
import {checkFeatureAuth} from "../App";
import {find, keyBy} from "lodash/collection";
import {BusRoute} from "../model/busRoute";
import {CopyToClipboard} from "react-copy-to-clipboard/lib/Component";
import {API} from "aws-amplify";
import {useAppContext} from "../libs/contextLib";

const {Text, Paragraph} = Typography;

const fileTypeOptions = [
    // {label: 'CSV', value: 'CSV', disabled: false, button: 'Download'},
    {label: 'TODIS', value: 'TODIS', disabled: false, button: 'Export to TfNSW'},
    {label: 'GTFS', value: 'GTFS', disabled: false, button: 'Download'},
    {label: 'XML', value: 'GTFS_TODIS', disabled: false, button: 'Download TODIS', beta: true},
    {label: 'HRM', value: 'HRM', disabled: false, button: 'Download'},
    {label: 'ICAL', value: 'ICAL', disabled: false, button: 'Download'},
    // {label: 'TransXChange', value: 'TransXChange', disabled: true, button: 'Download'},
]

// const actionOptions = [
//     {label: 'Download', value: 'Download', disabled: false, types: ['CSV', 'GTFS']},
//     {label: 'Publish', value: 'Publish', disabled: false, types: ['TfNSW']},
// ]

const MAX_TODIS_HISTORY_ITEMS = 5;

// Helper function to get validation checks description
const getValidationChecksDescription = () => {
    return [
        {
            title: "Route Validation",
            description: "Checking that all routes have valid route numbers and names",
            details: [
                "Route numbers must be alphanumeric and follow TfNSW format (e.g., 'M41', '333')",
                "Route names must be descriptive and match the actual service (e.g., 'Bondi Junction to Circular Quay')",
                "Routes must have at least one active service schedule",
                "Route direction must be clearly defined (inbound/outbound or clockwise/counterclockwise)",
                "Route type must be specified (regular, school, express, etc.)"
            ]
        },
        {
            title: "Stop Validation",
            description: "Verifying all stops have proper coordinates and names",
            details: [
                "Stop coordinates must be valid GPS coordinates within NSW boundaries",
                "Stop names must be unique and descriptive (e.g., 'Bondi Junction Station, Stand A')",
                "Stop codes must match TfNSW stop database when available",
                "Stops must have proper accessibility information (wheelchair access, etc.)",
            ]
        },
        {
            title: "Schedule Validation",
            description: "Ensuring all trips have valid departure and arrival times",
            details: [
                "All trips must have at least 2 stops (origin and destination)",
                "Departure times must be in 24-hour format (HH:MM:SS)",
                "Trip duration must be realistic (not negative or excessively long)",
                "Service frequency must be consistent with route type",
                "Weekday, weekend, and holiday schedules must be properly defined"
            ]
        },
        {
            title: "Stop Time Validation",
            description: "Checking that stop times are in chronological order",
            details: [
                "Stop times must progress chronologically from first to last stop",
                "Dwell times at stops must be realistic (minimum 10 seconds)",
                "Running times between stops must be physically possible",
                "No duplicate stop times within the same trip",
                "Stop times must align with published timetables"
            ]
        },
        {
            title: "Transfer Validation",
            description: "Verifying transfer connections between routes",
            details: [
                "Transfer stops must exist on both connecting routes",
                "Transfer times must allow for realistic passenger movement",
                "Transfer connections must be within reasonable walking distance",
                "Transfer zones must be properly defined for fare integration",
                "Transfer frequency must meet minimum service standards"
            ]
        },
        {
            title: "TfNSW Stop Validation",
            description: "Confirming stops exist in Transport for NSW database",
            details: [
                "All stops must be registered in the TfNSW stop database",
                "Stop codes must match official TfNSW stop codes",
                "Stop names must be consistent with TfNSW naming conventions",
                "Stops must be active in the current TfNSW system",
                "Stop hierarchy must be correctly maintained (parent/child stops)"
            ]
        },
        {
            title: "Distance Validation",
            description: "Checking stops are within 15 meters of TfNSW stops",
            details: [
                "GPS coordinates must be within 15 meters of official TfNSW stop locations",
                "Stop placement must be on the correct side of the road",
                "Stop accessibility must meet TfNSW standards",
                "Stop infrastructure must be properly documented",
                "Stop environment must be safe for passenger boarding/alighting"
            ]
        },
        {
            title: "Data Completeness",
            description: "Ensuring all required fields are present and valid",
            details: [
                "All mandatory TfNSW fields must be populated",
                "Data format must comply with TfNSW specifications",
                "Character encoding must be UTF-8 for proper text display",
                "Date formats must follow ISO 8601 standard",
                "File size must be within TfNSW upload limits"
            ]
        }
    ];
};

// Helper function to generate checklist steps based on status
const getTODISChecklistSteps = (status, validationSuccessful = false) => {
    const steps = [];
    
    // Always show step 1
    steps.push({
        title: (
            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                <span>Validate TODIS Data</span>
                <InfoCircleOutlined 
                    style={{color: '#1890ff', cursor: 'pointer'}}
                    onClick={(e) => {
                        e.stopPropagation();
                        Modal.info({
                            title: 'TODIS Validation Checks',
                            content: (
                                <div>
                                    <p style={{marginBottom: '16px'}}>
                                        The following validation checks are performed to ensure your data meets Transport for NSW requirements:
                                    </p>
                                    <div style={{maxHeight: '400px', overflowY: 'auto'}}>
                                        {getValidationChecksDescription().map((check, index) => (
                                            <div key={index} style={{marginBottom: '20px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '6px'}}>
                                                <div style={{fontWeight: '600', color: '#1890ff', marginBottom: '6px'}}>
                                                    {check.title}
                                                </div>
                                                <div style={{color: '#666', marginBottom: '8px', fontSize: '13px'}}>
                                                    {check.description}
                                                </div>
                                                <ul style={{paddingLeft: '20px', margin: '0', fontSize: '12px'}}>
                                                    {check.details.map((detail, detailIndex) => (
                                                        <li key={detailIndex} style={{marginBottom: '4px', color: '#333'}}>
                                                            {detail}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ),
                            width: 600,
                            okText: 'Got it'
                        });
                    }}
                />
            </div>
        ),
        description: 'Checking route data, stops, and schedules',
        status: status === 'Validating' ? 'process' : 
               status === 'Validated' || status === 'Bundling' || status === 'Submitted' || status === 'Converting' || status === 'Emailed' ? 'finish' : 
               status === 'Failed' || status === 'Timeout' ? 'error' : 'wait'
    });
    
    // Only show steps 2 and 3 if export has actually started (not just validation successful)
    if (status === 'Bundling' || status === 'Submitted' || status === 'Converting' || status === 'Emailed') {
        steps.push({
            title: 'Preparing TODIS Files',
            description: 'Creating TODIS ZIP files for each operator accreditation',
            status: status === 'Bundling' ? 'process' : 
                   status === 'Submitted' || status === 'Converting' || status === 'Emailed' ? 'finish' : 
                   status === 'Failed' || status === 'Timeout' ? 'error' : 'wait'
        });
        
        steps.push({
            title: 'Submit to TfNSW',
            description: 'Sending data to Transport for NSW',
            status: status === 'Submitted' ? 'process' : 
                   status === 'Converting' || status === 'Emailed' ? 'finish' : 
                   status === 'Failed' || status === 'Timeout' ? 'error' : 'wait'
        });
    }
    
    return steps;
};

// Helper function to get status color and text
const getStatusDisplay = (status) => {
    switch (status) {
        case 'Failed':
            return { color: 'error', text: 'Validation failed', icon: <CloseCircleOutlined /> };
        case 'Timeout':
            return { color: 'error', text: 'Submission timed out', icon: <ClockCircleOutlined /> };
        case 'Validating':
            return { color: 'warning', text: 'Validating', icon: <SyncOutlined spin /> };
        case 'Validated':
            return { color: 'success', text: 'Validation passed', icon: <CheckCircleOutlined /> };
        case 'Bundling':
            return { color: 'warning', text: 'Preparing', icon: <SyncOutlined spin /> };
        case 'Submitted':
            return { color: 'warning', text: 'Submitting', icon: <SyncOutlined spin /> };
        case 'Converting':
        case 'Emailed':
            return { color: 'success', text: 'Submitted to TfNSW', icon: <CheckCircleOutlined /> };
        default:
            return { color: 'default', text: status || 'Unknown', icon: <ClockCircleOutlined /> };
    }
};

export default function ExportModal({schedules, operator, setFaultState, isAdmin = false, messageApi, user, email, givenName, familyName}) {
    const context = useAppContext();
    const contextUser = context.user;
    
    // Use props if available, otherwise fall back to context
    const effectiveUser = user || contextUser;
    const effectiveEmail = email || context.email;
    const effectiveGivenName = givenName || context.givenName;
    const effectiveFamilyName = familyName || context.familyName;
    

    

    
    // Helper function to get user display name
    const getUserDisplayName = () => {
        // Try to construct full name from props or context
        if (effectiveGivenName && effectiveFamilyName) {
            const fullName = `${effectiveGivenName} ${effectiveFamilyName}`;
            return fullName;
        }
        
        // Try to get name from user object attributes
        if (effectiveUser?.attributes?.given_name && effectiveUser?.attributes?.family_name) {
            const fullName = `${effectiveUser.attributes.given_name} ${effectiveUser.attributes.family_name}`;
            return fullName;
        }
        
        // Fallback to email
        if (effectiveEmail) {
            return effectiveEmail;
        }
        
        // Try user object email
        if (effectiveUser?.attributes?.email) {
            return effectiveUser.attributes.email;
        }
        
        // Try user object username
        if (effectiveUser?.username) {
            return effectiveUser.username;
        }
        
        return 'Unknown User';
    };
    
    const [visible, setVisible] = useState(false);
    const [fileType, setFileType] = useState('TODIS');
    const [action, setAction] = useState('Download');
    const [isLoading, setIsLoading] = useState(false);
    // const [currentPublishStatus, setCurrentPublishStatus] = useState(null);
    const [isValidating, setIsValidating] = useState(false);
    const [publishEvents, setPublishEvents] = useState(null);
    const [publishHistoryItems, setPublishHistoryItems] = useState(null);
    const [tfnsw, setTfnsw] = useState(false)
    const [excludeInvalidGtfs, setExcludeInvalidGtfs] = useState(false)
    const [excludeInvalidTodis, setExcludeInvalidTodis] = useState(false)
    const [validationSuccessful, setValidationSuccessful] = useState(false)
    const [currentHistoryKey, setCurrentHistoryKey] = useState(null)
    const [expandedKeys, setExpandedKeys] = useState([])
    const [overrideValidations, setOverrideValidations] = useState(false)
    
    // Pagination state for TODIS history
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(5)

    // const getActions = useCallback(() => {
    //     const opts = [...actionOptions]
    //     opts.forEach(opt => {
    //         opt.disabled = !opt.types.includes(fileType)
    //     })
    //     return opts
    // }, [fileType])

    const loadPublishEvents = useCallback(async () => {
        console.log('Loading TODIS publish events')
        let events = await publishTfNSWModelData.fetchAll({asArray: false, apiKey: operator.companyId});
        console.log('TODIS publish events loaded', events)
        setPublishEvents(events);
    }, [operator.companyId, setPublishEvents]);

    // Add loading state for TODIS history
    const [isLoadingTODISHistory, setIsLoadingTODISHistory] = useState(false);
    
    // Track if there are any active validations to disable the Submit TODIS button
    const [hasActiveValidation, setHasActiveValidation] = useState(false);

    useEffect(() => {
        // Load TODIS history when modal opens and TODIS is selected
        if (visible && fileType === 'TODIS') {
            setIsLoadingTODISHistory(true);
            loadPublishEvents()
                .then(() => console.log('TODIS publish logs loaded'))
                .finally(() => setIsLoadingTODISHistory(false));
        }
        
        // Reset override validations when modal is closed
        if (!visible) {
            setOverrideValidations(false);
        }
    }, [visible, fileType, loadPublishEvents]);

    useEffect(() => {
        // Set up polling for TODIS updates when action is 'Export to TfNSW' or when there are optimistic events
        if ((action === 'Export to TfNSW' || hasActiveValidation) && visible && fileType === 'TODIS') {
            console.log('Setting up polling for TODIS updates')
            const timer = setInterval(() => {
                console.log('Polling for TODIS updates')
                loadPublishEvents().then(() => console.log('TODIS publish logs loaded'))
            }, 5000); // Faster polling for better responsiveness
            return () => clearInterval(timer);
        }
    }, [loadPublishEvents, action, fileType, operator, hasActiveValidation, visible]);

    const isActiveStatus = ({status, isOptimistic}) => {
        return status === 'Validating' || status === 'Bundling' || status === 'Sending' || isOptimistic
    }

    const isTimeout = ({createdAt, status}) => {
        return createdAt < Date.now() - 900000
    }

    useEffect(() => {
        if (!publishEvents) {
            return
        }
        let publishHistoryByKey = {};
        Object.keys(publishEvents).forEach(key => {
            const keyItems = key.split("#")
            if (last(keyItems) === 'current') {
                return
            }
            
            // Handle optimistic events (they have a different key format)
            if (key.startsWith('optimistic-')) {
                publishHistoryByKey[key] = [publishEvents[key]]
                return
            }
            
            const eventKey = keyItems.slice(0, -1).join("#");
            publishHistoryByKey[eventKey] = publishHistoryByKey[eventKey] || []
            publishHistoryByKey[eventKey].push(publishEvents[key])
        })

        const sortedKeys = Object.keys(publishHistoryByKey).sort((a, b) => publishHistoryByKey[b][0].createdAt - publishHistoryByKey[a][0].createdAt);

        // Create a new object and insert the keys in the sorted order
        const sortedObj = {};
        for (const key of sortedKeys) {
            sortedObj[key] = publishHistoryByKey[key];
        }
        
        // Check if validation was successful and track the current history key
        let hasSuccessfulValidation = false;
        let currentKey = null;
        
        Object.entries(sortedObj).forEach(([key, events]) => {
            if (events.some(event => event.status === 'Validated')) {
                hasSuccessfulValidation = true;
                currentKey = key;
            }
        });
        
        setValidationSuccessful(hasSuccessfulValidation);
        setCurrentHistoryKey(currentKey);
        
        // Check if there are any active validations (Validating, Bundling, Sending status or optimistic events)
        const hasActiveValidations = Object.entries(sortedObj).some(([key, events]) => {
            const lastEvent = events[events.length - 1];
            return isActiveStatus(lastEvent) && !isTimeout(lastEvent);
        });
        setHasActiveValidation(hasActiveValidations);
        
        // Auto-expand the newest history card if it's a new action
        const sortedKeysForExpansion = Object.keys(sortedObj).sort((a, b) => sortedObj[b][0].createdAt - sortedObj[a][0].createdAt);
        if (sortedKeysForExpansion.length > 0) {
            const newestKey = sortedKeysForExpansion[0];
            const newestEvent = sortedObj[newestKey][0];
            const isRecent = Date.now() - newestEvent.createdAt < 30000; // Within last 30 seconds
            
            // Auto-expand if it's a recent validating/bundling event or an optimistic event
            if (isRecent && (newestEvent.status === 'Validating' || newestEvent.status === 'Bundling' || newestEvent.isOptimistic)) {
                setExpandedKeys([newestKey]);
            }
        }
        
        // Calculate pagination
        const totalItems = Object.keys(sortedObj).length;
        const totalPages = Math.ceil(totalItems / pageSize);
        
        // If there are optimistic events and we're not on page 1, force to page 1
        const hasOptimisticEvents = Object.keys(sortedObj).some(key => sortedObj[key][0]?.isOptimistic);
        const effectivePage = hasOptimisticEvents ? 1 : currentPage;
        
        const startIndex = (effectivePage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedKeys = Object.keys(sortedObj).slice(startIndex, endIndex);
        
        // Create paginated object
        const paginatedObj = {};
        paginatedKeys.forEach(key => {
            paginatedObj[key] = sortedObj[key];
        });
        
        // Update current page if we forced it to page 1
        if (hasOptimisticEvents && currentPage !== 1) {
            setCurrentPage(1);
        }
        
        const historyItems = Object.keys(paginatedObj).map(key => {
            const publishedEvents = paginatedObj[key]
            const lastPublishedEvent = {...last(publishedEvents)}

            // Handle optimistic events with special styling
            let isOptimistic = lastPublishedEvent.isOptimistic;

            if(isActiveStatus(lastPublishedEvent) && isTimeout(lastPublishedEvent)) {
                lastPublishedEvent.status = 'Timeout'
                isOptimistic = false
            }
            
            const statusDisplay = getStatusDisplay(lastPublishedEvent.status)
            
            return {
                key,
                label:
                    <div style={{fontWeight: 500, opacity: isOptimistic ? 0.7 : 1}}>
                        {dayjs(lastPublishedEvent.createdAt).format("lll")}: 
                        <Tag color={statusDisplay.color} icon={statusDisplay.icon} className="todis-status-tag">
                            {isOptimistic ? 'Validating...' : statusDisplay.text}
                        </Tag>
                        {(lastPublishedEvent.userInfo?.name || (!lastPublishedEvent.userInfo && !isOptimistic)) && (
                            <span style={{fontSize: '12px', color: '#1890ff', marginLeft: '8px', fontWeight: '500'}}>
                                by {lastPublishedEvent.userInfo?.name || 'Unknown User'}
                            </span>
                        )}
                        {isOptimistic && <span style={{fontSize: '12px', color: '#8c8c8c', marginLeft: '8px'}}></span>}
                    </div>,
                children: <div key={'ffe' + key}>
                    <div className="todis-checklist">
                        <Steps 
                            direction="vertical" 
                            size="small"
                            items={getTODISChecklistSteps(lastPublishedEvent.status, validationSuccessful)}
                        />
                    </div>
                    
                    {/* Show Export button as part of the workflow after Step 1 */}
                    {lastPublishedEvent.status === 'Validated' && !isOptimistic && (
                        <div style={{marginTop: '8px', marginBottom: '16px', padding: '12px 16px', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '6px'}}>
                            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                <div>
                                    <div style={{fontWeight: 500, color: '#52c41a'}}>Validation Complete</div>
                                    <div style={{fontSize: '12px', color: '#8c8c8c', marginTop: '2px'}}>Ready to export to Transport for NSW</div>
                                </div>
                                <Button 
                                    type="primary" 
                                    className="btn-primary"
                                    feature={FEATURE.tfnsw}
                                    loading={isLoading}
                                    onClick={async (e) => {
                                        e.preventDefault();
                                        setIsLoading(true);
                                        
                                        // Collapse all history cards and prepare to expand the new one
                                        setExpandedKeys([]);
                                        
                                        try {
                                            if (!await checkFeatureAuth(setFaultState, operator, FEATURE.tfnsw)) {
                                                setFaultState({type: 'subscription', feature: FEATURE.tfnsw})
                                                setIsLoading(false);
                                                return
                                            }
                                            
                                            // Update the existing history card status to "Bundling"
                                            if (publishEvents && currentHistoryKey) {
                                                const updatedEvents = { ...publishEvents };
                                                const currentTime = Date.now();
                                                const userInfo = {name: getUserDisplayName()};
                                                const newEvent = {
                                                    status: 'Bundling',
                                                    createdAt: currentTime,
                                                    logId: `${currentHistoryKey}#${currentTime}`,
                                                    userInfo: userInfo
                                                };
                                                updatedEvents[`${currentHistoryKey}#${currentTime}`] = newEvent;
                                                setPublishEvents(updatedEvents);
                                            }
                                            
                                            const opts = {force: true, excludeInvalid: excludeInvalidTodis}
                                            const userInfo = {name: getUserDisplayName()};
                                            console.log(`TODIS Export initiated by: ${userInfo.name}`);
                                            await publishTfnsw({companyId: operator.companyId, opts, userInfo})
                                            setIsLoading(false);
                                        } catch (e) {
                                            setIsLoading(false);
                                            console.log(e, e)
                                            onError(e);
                                        }
                                    }}
                                    disabled={!validateForm()}
                                >
                                    Export to TfNSW
                                </Button>
                            </div>
                        </div>
                    )}
                    
                    {publishedEvents.filter(e => !e.isOptimistic).map(event =>
                        <div key={'slju' + event.createdAt} className="w-100">
                            <div className="row">
                                <div className="col-auto d-flex flex-column" style={{fontWeight: 400}}>
                                    {event.error ? (
                                        <div>
                                            <div>
                                                {formatTODISErrors(event.error, event.validations).map((error, i) => (
                                                    <div key={'error_' + i} style={{marginBottom: '16px'}}>
                                                        <div style={{fontWeight: 600, color: '#cf1322', marginBottom: '8px'}}>
                                                            {error.type}
                                                        </div>
                                                        <div style={{color: '#666', fontSize: '14px', marginBottom: '8px'}}>
                                                            {error.count} stop{error.count !== 1 ? 's' : ''} affected
                                                        </div>
                                                        <div style={{backgroundColor: '#fff2f0', border: '1px solid #ffccc7', borderRadius: '6px', padding: '12px'}}>
                                                            {error.stops.map((stop, j) => {
                                                                // Extract pointId from stop string (UUID format)
                                                                const pointIdMatch = stop.match(/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/);
                                                                const pointId = pointIdMatch ? pointIdMatch[1] : null;
                                                                // Only show the icon for point-related errors (type includes 'stop' or 'point')
                                                                const isPointError = error.type && /stop|point/i.test(error.type);
                                                                return (
                                                                    <div key={'stop_' + j} style={{marginBottom: '4px', fontSize: '13px', display: 'flex', alignItems: 'center'}}>
                                                                        <span>• {stop}</span>
                                                                        {isPointError && pointId && (
                                                                            <a
                                                                                href={`/planable/points/${pointId}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                style={{marginLeft: 8, color: '#1890ff'}}
                                                                                title="View this stop in Planable > Points"
                                                                                onClick={e => e.stopPropagation()}
                                                                            >
                                                                                <EyeOutlined />
                                                                            </a>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            <div style={{marginTop: '16px', marginBottom: '16px', pointerEvents: 'auto', padding: '12px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px'}}>
                                
                                                <Checkbox 
                                                    checked={overrideValidations} 
                                                    onChange={({target: {checked}}) => {
                                                        console.log('Checkbox clicked, new value:', checked);
                                                        setOverrideValidations(checked);
                                                    }}
                                                    onClick={(e) => {
                                                        console.log('Checkbox clicked directly');
                                                        e.stopPropagation();
                                                    }}
                                                    style={{marginBottom: '12px', pointerEvents: 'auto', zIndex: 1000}}
                                                >
                                                    Override validations
                                                </Checkbox>
                                                
                                                {overrideValidations && (
                                                    <div style={{marginBottom: '16px', padding: '12px', backgroundColor: '#fff7e6', border: '1px solid #ffd591', borderRadius: '6px'}}>
                                                        <div style={{fontSize: '12px', color: '#d46b08', marginBottom: '8px'}}>
                                                            ⚠️ Disclaimer: TODIS data validations will be overridden. This may have impacts with your TODIS submission to TfNSW and operational bus information.
                                                        </div>
                                                        <Button 
                                                            type="primary" 
                                                            className="btn-primary"
                                                            feature={FEATURE.tfnsw}
                                                            loading={isLoading}
                                                            onClick={async (e) => {
                                                                e.preventDefault();
                                                                setIsLoading(true);
                                                                
                                                                // Collapse all history cards and prepare to expand the new one
                                                                setExpandedKeys([]);
                                                                
                                                                try {
                                                                    if (!await checkFeatureAuth(setFaultState, operator, FEATURE.tfnsw)) {
                                                                        setFaultState({type: 'subscription', feature: FEATURE.tfnsw})
                                                                        setIsLoading(false);
                                                                        return
                                                                    }
                                                                    
                                                                    // Update the existing history card status to "Bundling"
                                                                    if (publishEvents && currentHistoryKey) {
                                                                        const updatedEvents = { ...publishEvents };
                                                                        const currentTime = Date.now();
                                                                        const userInfo = {name: getUserDisplayName()};
                                                                        const newEvent = {
                                                                            status: 'Bundling',
                                                                            createdAt: currentTime,
                                                                            logId: `${currentHistoryKey}#${currentTime}`,
                                                                            userInfo: userInfo
                                                                        };
                                                                        updatedEvents[`${currentHistoryKey}#${currentTime}`] = newEvent;
                                                                        setPublishEvents(updatedEvents);
                                                                    }
                                                                    
                                                                    const opts = {force: true, excludeInvalid: excludeInvalidTodis, overrideValidations: true}
                                                                    const userInfo = {name: getUserDisplayName()};
                                                                    console.log(`TODIS Export (with override) initiated by: ${userInfo.name}`);
                                                                    await publishTfnsw({companyId: operator.companyId, opts, userInfo})
                                                                    setIsLoading(false);
                                                                } catch (e) {
                                                                    setIsLoading(false);
                                                                    console.log(e, e)
                                                                    onError(e);
                                                                }
                                                            }}
                                                            disabled={false}
                                                        >
                                                            Export to TfNSW
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                            
                                            <div style={{display: 'flex', gap: '8px'}}>
                                                <Button 
                                                    onClick={() => {
                                                        const subject = 'TODIS errors';
                                                        const body = event.error;
                                                        const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                                                        window.open(mailtoLink);
                                                        messageApi.info('Email client opened with error details', 3);
                                                    }}
                                                >
                                                    Email Error
                                                </Button>
                                                <Button 
                                                    onClick={async () => {
                                                        try {
                                                            const csvData = await generateTODISErrorCSV(event.error, event.validations);
                                                            fileDownload(csvData, `TODIS_Validation_Errors_${dayjs().format("YYYY-MM-DD_HH:mm")}.csv`, 'text/csv');
                                                            messageApi.success('Error CSV downloaded', 3);
                                                        } catch (e) {
                                                            console.error('Error downloading CSV:', e);
                                                            messageApi.error('Failed to download CSV', 3);
                                                        }
                                                    }}
                                                    icon={<DownloadOutlined />}
                                                >
                                                    Download CSV
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (event.key || event.url) ? (
                                        <Button size="small" className="btn-secondary mt-1 mb-2"
                                                onClick={async () => {
                                                    event.key && fileDownload(`https://${config.s3.BUCKET}.s3.ap-southeast-2.amazonaws.com${event.key}`, `Busable_GTFS_${dayjs().format("YYYY-MM-DD_HH:mm")}.zip`, 'application/zip');
                                                    event.url && fileDownload(event.url, `Busable_TODIS_${dayjs().format("YYYY-MM-DD_HH:mm")}.zip`, 'application/zip');
                                                }}>
                                            <Download className="mr-2"/> {event.key ? 'GTFS' : 'TODIS'}
                                        </Button>
                                    ) : ''}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            }
        });
        
        setPublishHistoryItems({
            items: historyItems,
            pagination: {
                current: effectivePage,
                pageSize: pageSize,
                total: totalItems,
                totalPages: totalPages
            }
        });
    }, [publishEvents, setPublishHistoryItems, action, fileType, operator, currentPage, pageSize, overrideValidations])

    function validateForm() {
        return action && fileType;
    }

    async function toCsv(rows) {
        return new Promise((resolve, reject) => {
            csvStringify(rows, {
                header: true,
                delimiter: ','
            }, (err, output) => {
                if (err) {
                    console.log(`Error CSVing rows.`, err)
                    reject(err)
                }
                resolve(output)
            })
        })
    }

    // Function to format TODIS validation errors for better readability
    function formatTODISErrors(errorText, validations = null) {
        // If validations object is available, use it directly
        if (validations && typeof validations === 'object') {
            const formattedErrors = [];
            
            // Process each operator's validations
            Object.entries(validations).forEach(([operatorId, operatorValidations]) => {
                if (Array.isArray(operatorValidations)) {
                    operatorValidations.forEach(validation => {
                        // Parse structured format: TYPE:COUNT:OPERATOR:ITEMS
                        if (validation.includes(':') && validation.split(':').length >= 4) {
                            const parts = validation.split(':');
                            const errorType = parts[0];
                            const count = parseInt(parts[1]) || 0;
                            const operator = parts[2];
                            const items = parts.slice(3).join(':').split('|');
                            
                            // Map error types to user-friendly names
                            const errorTypeMap = {
                                'STOP_CODE_VIOLATIONS': 'Stop points are missing TfNSW stop codes or have duplicate stop codes',
                                'SHIFT_VIOLATIONS': 'Shifts are missing trip_id, driver_shift or service_id',
                                'MISSING_TFNSW_STOPS': 'Stop points are missing in TfNSW systems',
                                'STOP_DISTANCE_VIOLATIONS': 'Stop points are further than 15m from TfNSW stops'
                            };
                            
                            const formattedStops = items.map(stop => {
                                // Look for Point ID in parentheses or extract from stop name
                                const pointIdMatch = stop.match(/\(([^)]+)\)/);
                                const pointId = pointIdMatch ? pointIdMatch[1] : '';
                                const stopName = stop.replace(/\([^)]+\)/, '').trim();
                                
                                if (pointId) {
                                    // Check if it looks like a Point ID (long alphanumeric string)
                                    if (pointId.length > 10 && /^[A-Z0-9]+$/.test(pointId)) {
                                        return `${stopName} (Point ID: ${pointId})`;
                                    } else {
                                        return `${stopName} (${pointId})`;
                                    }
                                } else {
                                    return stopName;
                                }
                            });
                            
                            formattedErrors.push({
                                type: errorTypeMap[errorType] || errorType,
                                count: count,
                                operator: operator,
                                stops: formattedStops,
                                originalLine: validation
                            });
                        } else {
                            // Handle non-structured validations (fallback)
                            formattedErrors.push({
                                type: 'Validation Error',
                                count: 1,
                                operator: operatorId,
                                stops: [validation],
                                originalLine: validation
                            });
                        }
                    });
                }
            });
            
            return formattedErrors;
        }
        
        // Fallback to parsing error text if validations object is not available
        if (!errorText) return [];
        
        const lines = errorText.split('\n').filter(line => line.trim());
        const formattedErrors = [];
        
        lines.forEach((line, index) => {
            // Check if this is a structured format (TYPE:COUNT:OPERATOR:ITEMS)
            if (line.includes(':')) {
                // Fallback to old format parsing for backward compatibility
                const [errorType, details] = line.split(':').map(s => s.trim());
                if (details) {
                    // Parse stops more carefully to handle commas in stop names
                    // Split by comma, but only when not inside parentheses
                    const stopNames = details.split('),').map(s => s.trim() + ')');
                    const formattedStops = stopNames.map(stop => {
                        // Look for Point ID in parentheses or extract from stop name
                        const pointIdMatch = stop.match(/\(([^)]+)\)/);
                        const pointId = pointIdMatch ? pointIdMatch[1] : '';
                        const stopName = stop.replace(/\([^)]+\)/, '').trim();
                        
                        if (pointId) {
                            // Check if it looks like a Point ID (long alphanumeric string)
                            if (pointId.length > 10 && /^[A-Z0-9]+$/.test(pointId)) {
                                return `${stopName} (Point ID: ${pointId})`;
                            } else {
                                return `${stopName} (${pointId})`;
                            }
                        } else {
                            return stopName;
                        }
                    });
                    
                    // Clean up the error type by removing the count
                    const cleanErrorType = errorType.replace(/^\d+\s+/, '');
                    
                    formattedErrors.push({
                        type: cleanErrorType,
                        count: parseInt(errorType.match(/\d+/)?.[0] || '0'),
                        stops: formattedStops,
                        originalLine: line
                    });
                }
            }
        });
        
        return formattedErrors;
    }

    // Function to generate CSV from TODIS validation errors
    async function generateTODISErrorCSV(errorText, validations = null) {
        try {
            // Use validations object if available, otherwise parse error text
            let errors = [];
            
            if (validations) {
                // Use the structured validations object
                const formattedErrors = formatTODISErrors(errorText, validations);
                errors = formattedErrors.map(error => ({
                    'Error Type': error.type,
                    'Count': error.count,
                    'Operator': error.operator || 'Unknown',
                    'Affected Stops': error.stops.join('; '),
                    'Original Message': error.originalLine
                }));
            } else {
                // Fallback to parsing error text
                const lines = errorText.split('\n').filter(line => line.trim());
                
                lines.forEach((line, index) => {
                    if (line.includes(':')) {
                        const [errorType, details] = line.split(':').map(s => s.trim());
                        if (details) {
                            errors.push({
                                'Error Type': errorType,
                                'Details': details,
                                'Line Number': index + 1
                            });
                        }
                    }
                });
                
                // If no structured parsing, create a simple format
                if (errors.length === 0) {
                    errors.push({
                        'Error Message': errorText,
                        'Timestamp': dayjs().format('YYYY-MM-DD HH:mm:ss')
                    });
                }
            }
            
            return await toCsv(errors);
        } catch (e) {
            console.error('Error generating CSV:', e);
            // Fallback to simple format
            return await toCsv([{
                'Error Message': errorText,
                'Timestamp': dayjs().format('YYYY-MM-DD HH:mm:ss')
            }]);
        }
    }

    async function handleSubmit(event, validateOnly) {
        event.preventDefault();
        
        // Collapse all history cards and prepare to expand the new one
        setExpandedKeys([]);
        
        if (validateOnly) {
            setIsValidating(true);
        } else {
            setIsLoading(true);
        }

        try {
            // const operator = await getOperator(key);

            if (fileType === 'CSV') {

                const routes = await getRoutes(false, true, true, true, false, false, operator.operatorId);
                routes.forEach(route => {
                    if (schedules && route.scheduleId) {
                        route.schedule = schedules[route.scheduleId]
                    }
                })

                const rows = getRoutesAsFlatArray(routes)
                let output = await toCsv(rows)
                fileDownload(output, `Busable_CSV_${dayjs().format("YYYY-MM-DD_HH:mm")}.csv`);
                setIsLoading(false);
                setVisible(false)

            } else if (fileType === 'GTFS') {
                const userId = operator.operatorKey;
                const schedulesAsArray = values(schedules);
                console.log('Found %d schedules', schedulesAsArray.length)
                const stops = values(await stopModelData.getAll(userId)).filter(s => (!s.stopType || s.stopType === 'bus'));

                console.log('Found %d stops', stops.length)
                const stopsById = keyBy(stops, 'stopId');
                const routes = values(await routeModelData.getAll()).filter(r => r.published === 1).map(r => new BusRoute(r));
                console.log('Found %d routes', routes.length)

                const shiftBats = await shiftBatModelData.getAll({asArray: true});
                console.log('Building zip.')
                const zip = await buildGtfsZip(operator, routes, schedules, stopsById,
                    await driverShiftModelData.getAll(), await vehicleShiftModelData.getAll(),
                    shiftBats, await deadrunModelData.getAll(), {exts: {tfnsw: tfnsw}, excludeInvalid: excludeInvalidGtfs});
                console.log(zip);

                fileDownload(zip.toBuffer(), `Busable_GTFS-S_${dayjs().format("YYYY-MM-DD_HH:mm")}.zip`, 'application/zip');
                setIsLoading(false);
                setVisible(false)
            } else if (fileType === 'TODIS') {
                if (!await checkFeatureAuth(setFaultState, operator, FEATURE.tfnsw)) {
                    setFaultState({type: 'subscription', feature: FEATURE.tfnsw})
                    setIsLoading(false);
                    setIsValidating(false);
                    return
                }
                
                // Create optimistic update for immediate UI feedback
                const currentTime = Date.now();
                const optimisticEventKey = `optimistic-${currentTime}`;
                const userInfo = {name: getUserDisplayName()};
                const optimisticEvent = {
                    status: 'Validating',
                    createdAt: currentTime,
                    logId: optimisticEventKey,
                    isOptimistic: true, // Flag to identify optimistic updates
                    userInfo: userInfo // Include user info in optimistic event
                };
                
                // Immediately add optimistic event to UI and show loading state
                setPublishEvents(existingEvents => ({
                    ...existingEvents,
                    [optimisticEventKey]: optimisticEvent
                }));
                
                // Collapse all history cards and expand the new optimistic one
                setExpandedKeys([optimisticEventKey]);
                
                // Reset override validations when starting a new validation
                setOverrideValidations(false);
                
                // Show immediate feedback to user
                messageApi.info('TODIS validation started...', 2);
                
                let opts = {force: true, excludeInvalid: excludeInvalidTodis}
                if (validateOnly) {
                    opts = {force: false, validateOnly: true, excludeInvalid: excludeInvalidTodis}
                }
                
                try {
                    const actionType = validateOnly ? 'Submit TODIS' : 'Export to TfNSW';
                    console.log(`${actionType} initiated by: ${userInfo.name}`);
                    await publishTfnsw({companyId: operator.companyId, opts, userInfo})
                    
                    if (validateOnly) {
                        setIsValidating(false);
                    } else {
                        setIsLoading(false);
                    }
                } catch (error) {
                    console.error('Error publishing TODIS:', error);
                    // If API call fails, remove optimistic event and show error
                    setPublishEvents(existingEvents => {
                        const updatedEvents = { ...existingEvents };
                        delete updatedEvents[optimisticEventKey];
                        return updatedEvents;
                    });
                    setIsLoading(false);
                    throw error; // Re-throw to be caught by outer catch block
                }
            } else if (fileType === 'GTFS_TODIS') {
                const userId = operator.operatorKey;
                const schedules = values(await scheduleModelData.getAll());
                const stops = values(await stopModelData.getAll(userId)).filter(s => (!s.stopType || s.stopType === 'bus'));
                const stopsById = keyBy(stops, 'stopId');
                const routes = values(await routeModelData.getAll()).filter(r => r.published === 1).map(r => new BusRoute(r));
                const shiftBats = await shiftBatModelData.getAll({asArray: true});
                const zip = await buildTodisZip(
                    operator,
                    routes,
                    schedules,
                    stopsById,
                    await driverShiftModelData.getAll(),
                    await vehicleShiftModelData.getAll(),
                    shiftBats,
                    await deadrunModelData.getAll(),
                    {excludeInvalid: false}
                );
                fileDownload(zip.toBuffer(), `Busable_TODIS-GTFS-${dayjs().format("YYYY-MM-DD_HH:mm")}.zip`, 'application/zip');
                setIsLoading(false);
                setVisible(false)
            } else if (fileType === 'HRM') {
                const employees = values(await employeeModelData.getAll());
                const zip = await buildHRMtoZip(operator, employees);
                fileDownload(zip.toBuffer(), `Busable_HRM_${dayjs().format("YYYY-MM-DD_HH:mm")}.zip`, 'application/zip');
                setIsLoading(false);
                setVisible(false)
            } else if (fileType === 'ICAL') {
                const ical = await API.get('operators', `/ical?_k=${operator.operatorId}`);
                fileDownload(ical, `${operator.operatorId}_ICAL_${dayjs().format("YYYY-MM-DD_HH:mm")}.ics`, 'text/calendar');
                setIsLoading(false);
                setVisible(false)
            }

        } catch (e) {
            setIsLoading(false);
            console.log(e, e)
            onError(e);
        }

        // if (action === 'Publish') {
        //     setStatus("submitted")
        //     setTimeout(() => {
        //         setStatus("validating")
        //         setTimeout(() => {
        //             setIsLoading(false);
        //         }, 10000)
        //     }, 10000)
        //
        // }
    }

    return (
        <>
            {
                !isLoading ?
                    <Nav.Link onClick={() => setVisible(true)}>Export</Nav.Link>
                    :
                    <Nav.Link onClick={() => setVisible(true)}>
                        <SyncOutlined spin/> Export
                    </Nav.Link>
            }
            <Modal
                open={visible}
                width={800}
                title={`Export Data`}
                onCancel={() => {
                    setVisible(false)
                    setAction("Download");
                    setFileType("TODIS")
                }}
                destroyOnClose={true}
                footer={[
                    <Button key="back" className="btn-secondary" onClick={() => {
                        setVisible(false)
                        setAction("Download");
                        setFileType("TODIS")
                    }}>
                        Done
                    </Button>,
                    fileType === 'TODIS' && 
                    <Button key="validate" type="primary" className="btn-primary"
                            loading={isValidating}
                            onClick={e => {
                                handleSubmit(e, true).then(() => console.log('validated'))
                            }}>
                        Submit TODIS
                    </Button>,
                    fileType !== 'TODIS' && <Button key="submit" type="primary" className="btn-primary"
                            loading={isLoading}
                            onClick={handleSubmit}
                            disabled={!validateForm()}>
                        {action}
                    </Button>,
                ]}
            >
                <div className="Export">
                    <div className="row">
                        <div className="col-lg-12">
                            {/* <h6>File type {fileType}</h6> */}
                            <p>Export operational service information.</p>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-12">
                            <Radio.Group
                                options={fileTypeOptions.filter(opt => !opt.beta || isAdmin)}
                                onChange={({target: {value}}) => {
                                    setFileType(value)
                                    const opt = find(fileTypeOptions, opt => opt.label === value)
                                    setAction(opt.button)
                                }}
                                value={fileType}
                                optionType="button"
                                buttonStyle="solid"
                                disabled={isLoading}
                            />
                        </div>
                    </div>
                    {/*<div className="row" style={{marginTop: "15px"}}>*/}
                    {/*    <div className="col-lg-12">*/}
                    {/*        <h6>Action</h6>*/}
                    {/*    </div>*/}
                    {/*</div>*/}
                    {/*<div className="row">*/}
                    {/*    <div className="col-lg-12">*/}
                    {/*        <Radio.Group*/}
                    {/*            options={getActions()}*/}
                    {/*            onChange={({target: {value}}) => setAction(value)}*/}
                    {/*            value={action}*/}
                    {/*            disabled={isLoading}*/}
                    {/*        />*/}
                    {/*    </div>*/}
                    {/*</div>*/}
                    {
                        <div className="row" style={{margin: "20px 0"}}>
                            {/*<div className="col-lg-12"><h6>Status: {currentPublishStatus.status} on {dayjs(parseInt(currentPublishStatus.logId.split("#")[4])).format('lll')}</h6> {currentPublishStatus.status === 'Success' && currentPublishStatus.url && <a href={currentPublishStatus.url}>Download</a>}</div>*/}
                            {fileType === 'TODIS' ? (
                                <div className={"w-100"}>
                                    <div className={"mb-2"}>
                                        <div><h6 className="mt-3">Exclude invalid stop times</h6></div>
                                        <div><Checkbox checked={excludeInvalidTodis} onChange={({target: {checked}}) => {
                                            setExcludeInvalidTodis(checked)
                                        }}>Exclude</Checkbox></div>
                                    </div>
                                    
                                    {isLoadingTODISHistory ? (
                                        <div style={{textAlign: 'center', padding: '40px 20px'}}>
                                            <SyncOutlined spin style={{fontSize: '24px', color: '#1890ff', marginBottom: '12px'}} />
                                            <div style={{color: '#666', fontSize: '14px'}}>Loading TODIS history...</div>
                                        </div>
                                    ) : publishHistoryItems?.items ? (
                                        <div>
                                            <Collapse 
                                                className="w-100" 
                                                items={publishHistoryItems.items}
                                                activeKey={expandedKeys}
                                                onChange={setExpandedKeys}
                                            />
                                            
                                            {/* Pagination Controls */}
                                            {publishHistoryItems.pagination && (
                                                <div style={{marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                                        <span style={{fontSize: '12px', color: '#666'}}>Show:</span>
                                                        <select 
                                                            value={pageSize} 
                                                            onChange={(e) => {
                                                                setPageSize(Number(e.target.value));
                                                                setCurrentPage(1); // Reset to first page when changing page size
                                                            }}
                                                            style={{padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: '4px'}}
                                                        >
                                                            <option value={5}>5</option>
                                                            <option value={10}>10</option>
                                                            <option value={20}>20</option>
                                                            <option value={50}>50</option>
                                                        </select>
                                                        <span style={{fontSize: '12px', color: '#666'}}>per page</span>
                                                    </div>
                                                    
                                                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                                        <span style={{fontSize: '12px', color: '#666'}}>
                                                            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, publishHistoryItems.pagination.total)} of {publishHistoryItems.pagination.total} records
                                                        </span>
                                                        
                                                        <div style={{display: 'flex', gap: '4px'}}>
                                                            <Button 
                                                                size="small"
                                                                disabled={currentPage === 1}
                                                                onClick={() => setCurrentPage(currentPage - 1)}
                                                            >
                                                                Previous
                                                            </Button>
                                                            <span style={{padding: '4px 8px', fontSize: '12px'}}>
                                                                {publishHistoryItems.pagination.totalPages > 1 
                                                                    ? `Page ${currentPage} of ${publishHistoryItems.pagination.totalPages}`
                                                                    : `All ${publishHistoryItems.pagination.total} records`
                                                                }
                                                            </span>
                                                            <Button 
                                                                size="small"
                                                                disabled={currentPage === publishHistoryItems.pagination.totalPages}
                                                                onClick={() => setCurrentPage(currentPage + 1)}
                                                            >
                                                                Next
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div style={{textAlign: 'center', padding: '20px', color: '#999', fontSize: '14px'}}>
                                            No TODIS history found
                                        </div>
                                    )}
                                </div>
                            ) : fileType === 'GTFS' ? (
                                <Flex justify={"flex-start"}>
                                    <div className={"mr-4"}>
                                        <div><h6 className="mt-3">GTFS Extensions</h6></div>
                                        <div><Checkbox checked={tfnsw} onChange={({target: {checked}}) => {
                                            setTfnsw(checked)
                                        }}>Busable</Checkbox></div>
                                    </div>
                                    <div>
                                        <div><h6 className="mt-3">Exclude invalid stop times</h6></div>
                                        <div><Checkbox checked={excludeInvalidGtfs} onChange={({target: {checked}}) => {
                                            setExcludeInvalidGtfs(checked)
                                        }}>Exclude</Checkbox></div>
                                    </div>
                                </Flex>
                            ) : (
                                <></>
                            )}
                        </div>
                    }
                    {/*{*/}
                    {/*    action === 'Publish' && isLoading &&*/}
                    {/*    <div className="row" style={{marginTop: "15px"}}>*/}
                    {/*        <div className="col-lg-12">*/}
                    {/*            <h6>Status: <span className="text-muted">{status}</span></h6>*/}
                    {/*        </div>*/}
                    {/*    </div>*/}
                    {/*}*/}
                </div>
            </Modal>
        </>
    );
}
